/**
 * Agreement Rate Calculator Utility
 * Calculates agreement rates for evaluation forms based on AI scoring and EA suggestions
 */

export interface FormAggregationData {
    eaAcceptedSuggestionCount: number;
    eaSuggestionCount: number;
    aiAcceptedSuggestionCount: number;
    aiSuggestionCount: number;
}

export interface QuestionAggregationData {
    aiScoredTrue: number;
    aiScoredFalse: number;
    eaScoredTrue: number;
    eaScoredFalse: number;
}

export interface AgreementRateResult {
    agreementRate: number; // 0-1 decimal representation
    totalSuggestions: number;
    acceptedSuggestions: number;
    hasData: boolean;
}

/**
 * Agreement Rate Calculator class for form-level calculations
 */
export class AgreementRateCalculator {
    /**
     * Calculates form-level agreement rate using the formula:
     * (eaAcceptedSuggestionCount + aiAcceptedSuggestionCount) / (eaSuggestionCount + aiSuggestionCount)
     *
     * @param aggregationData - Form aggregation data from API
     * @returns Agreement rate result with calculated percentage and metadata
     */
    static calculateFormAgreementRate(
        aggregationData: FormAggregationData
    ): AgreementRateResult {
        const {
            eaAcceptedSuggestionCount,
            eaSuggestionCount,
            aiAcceptedSuggestionCount,
            aiSuggestionCount,
        } = aggregationData;

        const totalSuggestions = eaSuggestionCount + aiSuggestionCount;
        const acceptedSuggestions =
            eaAcceptedSuggestionCount + aiAcceptedSuggestionCount;

        // Handle case where there are no suggestions
        if (totalSuggestions === 0) {
            return {
                agreementRate: 0,
                totalSuggestions: 0,
                acceptedSuggestions: 0,
                hasData: false,
            };
        }

        const agreementRate = acceptedSuggestions / totalSuggestions;

        return {
            agreementRate: Math.max(0, Math.min(1, agreementRate)), // Ensure 0-1 range
            totalSuggestions,
            acceptedSuggestions,
            hasData: true,
        };
    }

    /**
     * Calculates question-level agreement rate using the formula:
     * (count where aiScored=true + count where eaScored=true) /
     * (count where aiScored=true + count where aiScored=false + count where eaScored=true + count where eaScored=false)
     *
     * @param aggregationData - Question aggregation data from API
     * @returns Agreement rate result with calculated percentage and metadata
     */
    static calculateQuestionAgreementRate(
        aggregationData: QuestionAggregationData
    ): AgreementRateResult {
        const { aiScoredTrue, aiScoredFalse, eaScoredTrue, eaScoredFalse } =
            aggregationData;

        const totalSuggestions =
            aiScoredTrue + aiScoredFalse + eaScoredTrue + eaScoredFalse;
        const acceptedSuggestions = aiScoredTrue + eaScoredTrue;

        // Handle case where there are no evaluations
        if (totalSuggestions === 0) {
            return {
                agreementRate: 0,
                totalSuggestions: 0,
                acceptedSuggestions: 0,
                hasData: false,
            };
        }

        const agreementRate = acceptedSuggestions / totalSuggestions;

        return {
            agreementRate: Math.max(0, Math.min(1, agreementRate)), // Ensure 0-1 range
            totalSuggestions: totalSuggestions,
            acceptedSuggestions,
            hasData: true,
        };
    }
}
